<template>
    <div ref="refCustomTagsAndFieldsRichText">
        <SelectRichText
            ref="refTriggerCustomTagsAndFieldsRichText"
            :label="$t.__('richtext.CustomTagsAndFieldsRichText.select_richtext_label')"
            :disabled="disabled || isLoadingCustomTagsAndFields"
            data-bot="custom-tags-and-fields-richtext__insert"
            @click="handleSelectRichText"
        />

        <CompassDropdown
            v-if="!isLoadingCustomTagsAndFields"
            ref="refCustomTagsAndFieldsDropdown"
            parentRef="refTriggerCustomTagsAndFieldsRichText"
            :isVisible="displayDropdownCustomTagsAndFields"
            filterable
            :noResultsLabel="$t.__('common.labels.no_result')"
            dropdownClass="custom-tags-and-fields-richtext__dropdown"
        >
            <CompassTooltip
                v-if="enableAI && isSellsyAIEnabled"
                :content="$t.__('richtext.CustomTagsAndFieldsRichText.tooltip')"
                :disabled="!templateUsedInAutomation"
                contentClass="custom-tags-and-fields-richtext__ai-tooltip"
                placement="right"
            >
                <CompassDropdownOption
                    :label="$t.__('common.labels.tags_ai')"
                    value="ai"
                    class="custom-tags-and-fields-richtext__ai"
                    :disabled="templateUsedInAutomation"
                    @itemSelected="openCustomTagsAIModal"
                >
                    <span
                        class="custom-tags-and-fields-richtext__ai-label"
                        :class="{ 'typographies-paragraph-medium-bold': !templateUsedInAutomation }"
                    >
                        {{ $t.__('common.labels.tags_ai') }}
                    </span>
                    <CompassIcon
                        icon="ai-sparkles-simple"
                        class="custom-tags-and-fields-richtext__ai-icon"
                    />
                </CompassDropdownOption>
            </CompassTooltip>
            <CompassOptionGroup
                v-for="(group, index) in formattedTags"
                :key="index"
                :label="group.label"
            >
                <CompassDropdownOption
                    v-for="(option, indexoption) in group.options"
                    :key="indexoption"
                    :label="option.label"
                    :value="option.value"
                    :labelCaption="option.caption"
                    :selected="false"
                    @itemSelected="addTagOrField(option)"
                />
            </CompassOptionGroup>
        </CompassDropdown>

        <CustomTagsAIModal
            v-if="showCustomTagsAIModal"
            :data="dataCustomTagsAIModal"
            :editor="editor"
            @addTagAI="addTagAI"
        />
    </div>
</template>

<script>
import OptionsRichTextMixin from 'mixins/OptionsRichTextMixin';
import SelectRichText from 'vcomponents/richtext/customoptions/SelectRichText.vue';
import { getEmailTemplateTags } from 'api/emailEngine/emailEngineService';
import Feature from 'Services/Feature';
import CustomTagsAIModal from 'vcomponents/richtext/CustomTagsAIModal.vue';

import { createNamespacedHelpers } from 'vuex';
const { mapState: mapStateSystem } = createNamespacedHelpers('system');

export default {
    name: 'CustomTagsAndFieldsRichText',
    components: {
        SelectRichText,
        CustomTagsAIModal,
    },
    mixins: [OptionsRichTextMixin],
    inject: ['parentHash', 'refRichText'],
    data() {
        return {
            displayDropdownCustomTagsAndFields: false,
            isLoadingCustomTagsAndFields: false,
            categoryMapping: {
                account: this.$t.__('common.labels.my_company'),
                company: this.$t.__('common.labels.company'),
                opportunity: this.$t.__('common.labels.opportunity'),
                staff: this.$t.__('common.labels.staff'),
                contact: this.$t.__('common.labels.contact'),
                document: this.$t.__('common.labels.document'),
                connection_address: this.$t.__('common.labels.connection_address'),
            },
            customTagsAndFields: [],
            enableAI: false,
            showCustomTagsAIModal: false,
            dataCustomTagsAIModal: null,
            templateUsedInAutomation: false,
        };
    },
    computed: {
        ...mapStateSystem(['errorsHandler']),
        isSellsyAIEnabled() {
            return Feature.isEnabled('sellsy-ai');
        },
        formattedTags() {
            const formattedData = Object.keys(this.categoryMapping).map((categoryKey) => ({
                label: this.categoryMapping[categoryKey],
                options: [],
            }));

            for (const item of this.customTagsAndFields) {
                const categoryLabel = this.categoryMapping[item.category];
                if (categoryLabel) {
                    const categoryObject = formattedData.find(
                        (data) => data.label === categoryLabel,
                    );
                    if (categoryObject) {
                        const option = { label: item.name, value: item.key };
                        if (item.group === 'customfield') {
                            option.caption = this.$t.__('common.labels.customfield');
                        }
                        categoryObject.options.push(option);
                    }
                }
            }
            return formattedData.filter((category) => category.options.length > 0);
        },
    },
    created() {
        this.getCustomTagsAndFiels();

        EventBus.on('customTagsAndFieldsRichText:closeModal', this.closeModal);
        EventBus.on('customTagsAndFieldsRichText:editTagAI', this.editTagAI);
    },
    mounted() {
        this.checkRefRichTextAndEnableAI();
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
        this.dataCustomTagsAIModal = null;
        EventBus.off('customTagsAndFieldsRichText:closeModal', this.closeModal);
        EventBus.off('customTagsAndFieldsRichText:editTagAI', this.editTagAI);
    },
    methods: {
        getCustomTagsAndFiels() {
            this.isLoadingCustomTagsAndFields = true;
            getEmailTemplateTags()
                .then((response) => {
                    this.customTagsAndFields = response.data;
                })
                .catch((error) => {
                    this.customTagsAndFields = [];
                    this.errorsHandler.logException(error);
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$t.__('email.SettingsEmailsTemplates.error_text_get_tags'),
                        close: true,
                    });
                })
                .finally(() => {
                    this.isLoadingCustomTagsAndFields = false;
                });
        },
        addTagOrField(option) {
            const tagFieldProps = {
                type: 'default',
                label: option.label,
                tag: option.value,
                isDisabled: this.disabled,
            };
            this.editor.commands.insertCustomTagsAndFields(tagFieldProps);
            this.displayDropdownCustomTagsAndFields = false;
        },
        checkRefRichTextAndEnableAI() {
            const template =
                window.sellsyapp.config.globalProperties.$store.state[this.parentHash]?.template;

            if (this.refRichText === 'refSettingsEmailsTemplatesFormRichTextMessage') {
                this.enableAI = true;

                if (this.parentHash && template && template.is_used_in_automation) {
                    this.templateUsedInAutomation = true;
                }
            }
        },
        handleSelectRichText() {
            if (this.disabled) return;
            this.displayDropdownCustomTagsAndFields = !this.displayDropdownCustomTagsAndFields;
        },
        handleClickOutside(event) {
            const selectRichtext = this.$refs.refTriggerCustomTagsAndFieldsRichText;
            const dropdown = this.$refs.refCustomTagsAndFieldsDropdown;
            const resetInputButton = event.target.closest(
                '.compass-dropdown--filterable__input-reset-icon',
            );
            if (dropdown && !selectRichtext.$el.contains(event.target) && !resetInputButton) {
                if (!dropdown.$el.contains(event.target)) {
                    this.displayDropdownCustomTagsAndFields = false;
                }
            }
        },
        openCustomTagsAIModal() {
            this.displayDropdownCustomTagsAndFields = false;
            this.dataCustomTagsAIModal = null;
            this.showCustomTagsAIModal = true;
        },
        addTagAI(option) {
            const tagAIProps = {
                type: 'ai',
                label: option.label,
                prompt: option.prompt,
                hash: option.hash,
                isDisabled: this.disabled,
            };
            const parentEditor = option.editor ?? this.editor;
            this.displayDropdownCustomTagsAndFields = false;
            parentEditor.commands.insertCustomTagsAI(tagAIProps);
        },

        editTagAI(data) {
            this.showCustomTagsAIModal = true;
            this.dataCustomTagsAIModal = data;
        },

        closeModal() {
            this.showCustomTagsAIModal = false;
        },
    },
};
</script>
<style lang="scss">
.compass-dropdown.custom-tags-and-fields-richtext {
    &__dropdown {
        z-index: 1001;

        .compass-dropdown-option--selected {
            .compass-dropdown-option--selected {
                display: none;
            }
        }

        .compass-tooltip,
        .compass-tooltip__element {
            display: block;
        }
    }
}

.custom-tags-and-fields-richtext__ai-tooltip {
    max-width: 288px;
}
</style>
<style lang="scss" scoped>
.custom-tags-and-fields-richtext {
    &__popover {
        display: flex;
        width: 312px;
    }
    &__ai {
        &:not(.compass-dropdown-option--disabled) {
            .custom-tags-and-fields-richtext__ai-label {
                color: var(--colors-sellsy-ai-50);
            }
        }
        &-icon {
            align-items: center;
            background: var(--colors-sellsy-ai-05);
            border-radius: 50%;
            display: inline-flex;
            height: 20px;
            justify-content: center;
            width: 20px;
        }

        :deep(.compass-icon) {
            path {
                fill: var(--colors-sellsy-ai-50);
            }
        }
    }
}
</style>
